import { ActionReducer, ActionReducerMap, MetaReducer } from '@ngrx/store';
import { appFeatureKey, appReducer, AppState } from "./app/app.reducer";
import { userFeatureKey, userReducer, UserState } from "./user/user.reducer";
import { dealerFeatureKey, dealerReducer, DealerState } from "./dealer/dealer.reducer";
import { equipmentFeatureKey, equipmentReducer, EquipmentState } from "./equipment/equipment.reducer";
import { dashboardFeatureKey, dashboardReducer, DashboardState } from "./dashboard/dashboard.reducer";
import { accountsFeatureKey, accountsReducer, AccountsState } from "./account/account.reducer";
import { eventFeatureKey, eventReducer, EventState } from "./event/event.reducer";
import { apikeyFeatureKey, apikeyReducer, ApikeyState } from "./apikey/apikey.reducer";
import { webhookFeatureKey, webhookReducer, WebhookState } from "./webhook/webhook.reducer";
import { forwarderFeatureKey, forwarderReducer, ForwarderState } from "./forwarder/forwarder.reducer";
import { clickFeatureKey, clickReducer, ClickState } from "./click/click.reducer";

export interface RootState {
  [accountsFeatureKey]: AccountsState,
  [dashboardFeatureKey]: DashboardState,
  [dealerFeatureKey]: DealerState,
  [equipmentFeatureKey]: EquipmentState,
  [appFeatureKey]: AppState,
  [userFeatureKey]: UserState,
  [eventFeatureKey]: EventState,
  [apikeyFeatureKey]: ApikeyState,
  [webhookFeatureKey]: WebhookState,
  [forwarderFeatureKey]: ForwarderState,
  [clickFeatureKey]: ClickState
}

export const rootReducer: ActionReducerMap<RootState> = {
  [accountsFeatureKey]: accountsReducer,
  [dashboardFeatureKey]: dashboardReducer,
  [dealerFeatureKey]: dealerReducer,
  [equipmentFeatureKey]: equipmentReducer,
  [appFeatureKey]: appReducer,
  [userFeatureKey]: userReducer,
  [eventFeatureKey]: eventReducer,
  [apikeyFeatureKey]: apikeyReducer,
  [webhookFeatureKey]: webhookReducer,
  [forwarderFeatureKey]: forwarderReducer,
  [clickFeatureKey]: clickReducer,
}

const STORAGE_KEY = 'appState';

function readFromStorage(): Partial<RootState> | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const fromLocal = window.localStorage.getItem(STORAGE_KEY);
    if (fromLocal) return JSON.parse(fromLocal) as Partial<RootState>;

    const fromSession = window.sessionStorage.getItem(STORAGE_KEY);
    return fromSession ? (JSON.parse(fromSession) as Partial<RootState>) : undefined;
  } catch {
    return undefined;
  }
}

function writeToStorage(state: RootState): void {
  if (typeof window === 'undefined') return;
  try {
    const remembered = state[appFeatureKey]?.rememberedUser;
    const useLocal = typeof remembered === 'string';

    const target = useLocal ? window.localStorage : window.sessionStorage;
    (useLocal ? window.sessionStorage : window.localStorage).removeItem(STORAGE_KEY);

    target.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { }
}

export function storageSyncMetaReducer<T extends RootState>(
  reducer: ActionReducer<T>
): ActionReducer<T> {
  return function (state: T | undefined, action: any): T {
    const isInit =
      action.type === '@ngrx/store/init' || action.type === '@ngrx/effects/init';

    if (isInit) {
      const rehydrated = readFromStorage();
      const next = reducer(rehydrated as T | undefined, action);
      writeToStorage(next as RootState);
      return next;
    }

    const next = reducer(state, action);
    writeToStorage(next as RootState);
    return next;
  };
}

export const metaReducers: MetaReducer<RootState>[] = [storageSyncMetaReducer];